import { Services } from '@Shared.Angular/@types/services';
import angular from 'angular';
import {
  IAddressSearchResult,
  IAddressDetail,
  IAddressProvider
} from '@Shared.Angular/@types/address';

export default class AddressLookupService {
  public static $inject = ['$http', '$q', 'APP_CONFIG'];

  private readonly AUTHENTICATED_API: string;
  private readonly PUBLIC_FORM_API: string;

  constructor(
    private $http: angular.IHttpService,
    private $q: angular.IQService,
    private APP_CONFIG: Services.APP_CONFIG
  ) {
    this.AUTHENTICATED_API = APP_CONFIG.apiBaseUrl + 'addresses';
    this.PUBLIC_FORM_API = APP_CONFIG.apiBaseUrl + 'form/addresses';
  }

  public getProviders(): angular.IPromise<IAddressProvider[]> {
    return this.$http
      .get<IAddressProvider[]>(`${this.AUTHENTICATED_API}/providers`)
      .then((response) => response.data)
      .catch((error) => {
        console.error('Failed to get address providers:', error);
        return [];
      });
  }

  public searchAddresses(
    query: string,
    provider: number,
    skip = 0,
    limit = 10
  ): angular.IPromise<IAddressSearchResult[]> {
    const params: Record<string, string | number> = {
      query: query,
      provider: provider,
      skip: skip,
      limit: limit
    };

    return this.$http
      .get<IAddressSearchResult[]>(`${this.AUTHENTICATED_API}`, { params })
      .then((response) => response.data)
      .catch((error) => {
        console.error('Address search failed:', error);
        return [];
      });
  }

  public searchAddressesForPublicForm(
    query: string,
    provider: number,
    businessId: string,
    skip = 0,
    limit = 10
  ): angular.IPromise<IAddressSearchResult[]> {
    const params: Record<string, string | number> = {
      query: query,
      provider: provider,
      businessId: businessId,
      skip: skip,
      limit: limit
    };

    return this.$http
      .get<IAddressSearchResult[]>(`${this.PUBLIC_FORM_API}`, { params })
      .then((response) => response.data)
      .catch((error) => {
        console.error('Address search failed:', error);
        return [];
      });
  }

  public getAddressDetails(
    addressId: string,
    provider: number
  ): angular.IPromise<IAddressDetail> {
    if (!addressId) {
      return this.$q.reject('Address ID is required');
    }

    const params: Record<string, string | number> = {
      provider: provider
    };

    return this.$http
      .get<IAddressDetail>(
        `${this.AUTHENTICATED_API}/${encodeURIComponent(addressId)}`,
        {
          params
        }
      )
      .then((response) => response.data)
      .catch((error) => {
        console.error('Failed to get address details:', error);
        throw error;
      });
  }

  public getAddressDetailsForPublicForm(
    addressId: string,
    provider: number,
    businessId: string
  ): angular.IPromise<IAddressDetail> {
    if (!addressId) {
      return this.$q.reject('Address ID is required');
    }

    const params: Record<string, string | number> = {
      provider: provider,
      businessId: businessId
    };

    return this.$http
      .get<IAddressDetail>(
        `${this.PUBLIC_FORM_API}/${encodeURIComponent(addressId)}`,
        {
          params
        }
      )
      .then((response) => response.data)
      .catch((error) => {
        console.error('Failed to get address details:', error);
        throw error;
      });
  }

  public formatAddress(address: IAddressDetail): string {
    if (!address) return '';

    const parts: string[] = [];

    if (address.street) parts.push(address.street);
    if (address.suburb) parts.push(address.suburb);
    if (address.town) parts.push(address.town);
    if (address.region) parts.push(address.region);
    if (address.postCode) parts.push(address.postCode);

    return parts.join(', ');
  }

  public isValidAddress(value: { addressId?: string } | null): boolean {
    return !!value && !!value.addressId;
  }
}

angular
  .module('flowingly.services')
  .service('addressLookupService', AddressLookupService);

export type AddressLookupServiceType = InstanceType<
  typeof AddressLookupService
>;
